import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { ColumnDefinition, ProcessedTableCell } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColumnDefinitionBulkEditInputV1 } from '@seeqdev/api/model/ColumnDefinitionBulkEditInputV1';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';

export interface EditTemplatePreviewState {
  previewCellsByCellKey: Record<string, ProcessedTableCell>;
  previewTableColumns: ColumnDefinition[];
  previewColumns: (ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1)[] | undefined;
}

export interface EditTemplatePreviewActions {
  setPreviewCells: (previewCellsByCellKey: Record<string, ProcessedTableCell>) => void;
  setPreviewTableColumns: (previewTableColumns: ColumnDefinition[]) => void;
  setPreviewColumns: (columns: (ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1)[] | undefined) => void;
  mergePreviewCells: (additions: Record<string, ProcessedTableCell>) => void;
  reset: (overrides?: Partial<EditTemplatePreviewState>) => void;
}

const initialState: EditTemplatePreviewState = Object.freeze({
  previewCellsByCellKey: {},
  previewTableColumns: [],
  previewColumns: undefined,
});

const editTemplatePreviewActionsFactory: ActionsFactory<EditTemplatePreviewState, EditTemplatePreviewActions> = (
  set,
  _get,
  resetState,
) => ({
  setPreviewCells: (previewCellsByCellKey) =>
    set((draft) => {
      draft.previewCellsByCellKey = previewCellsByCellKey;
    }),

  setPreviewTableColumns: (previewTableColumns) =>
    set((draft) => {
      draft.previewTableColumns = previewTableColumns;
    }),

  setPreviewColumns: (columns) =>
    set((draft) => {
      draft.previewColumns = columns;
    }),

  mergePreviewCells: (additions) =>
    set((draft) => {
      Object.assign(draft.previewCellsByCellKey, additions);
    }),

  reset: resetState,
});

const STORE_NAME = 'EditTemplatePreview';

const [EditTemplatePreviewStoreProvider, useEditTemplatePreviewStoreInstance, useEditTemplatePreviewSelector] =
  createStore(STORE_NAME, initialState, editTemplatePreviewActionsFactory);

export { EditTemplatePreviewStoreProvider };

export const useEditTemplatePreviewCells = () => useEditTemplatePreviewSelector((state) => state.previewCellsByCellKey);
export const useEditTemplatePreviewTableColumns = () =>
  useEditTemplatePreviewSelector((state) => state.previewTableColumns);
export const useEditTemplatePreviewColumns = () => useEditTemplatePreviewSelector((state) => state.previewColumns);

export const useEditTemplatePreviewCellByKey = (cellKey: string | undefined): ProcessedTableCell | undefined =>
  useEditTemplatePreviewSelector((state) => (cellKey === undefined ? undefined : state.previewCellsByCellKey[cellKey]));

export const useEditTemplatePreviewActions = () => useEditTemplatePreviewStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` and mutate via `store.actions.*`.
 * New code should use the hooks above and the Provider.
 */
export function legacyCreateEditTemplatePreviewStore() {
  return createStoreInstance<EditTemplatePreviewState, EditTemplatePreviewActions>(
    initialState,
    editTemplatePreviewActionsFactory,
    { storeName: STORE_NAME },
  );
}
